import { inject } from 'mobx-react';
import { useHistory, useLocation } from 'react-router-dom';
import * as React from 'react';

import { AnalyticsStore } from 'bernie-core';
import { ClickTracker } from 'bernie-analytics';

import { useLocalization } from '@shared-ui/localization-context';

import { UIPageStore } from 'stores/ui-page-store';

import { MessagingCardViewModel } from 'models';

import { UitkCard } from 'uitk-react-cards';
import { UitkFigure, UitkFigureAspectRatioType } from 'uitk-react-images';
import { UitkHeading } from 'uitk-react-text';
import { UitkIcon, UitkIconSize } from 'uitk-react-icons';
import { UitkLayoutFlex } from 'uitk-react-layout-flex';
import { UitkLink } from 'uitk-react-link';
import { UitkList, UitkListItem } from 'uitk-react-list';
import { UitkMark, UitkMarkSize } from 'uitk-react-mark';
import { UitkSpacing } from 'uitk-react-spacing';

import { DoubleLink } from 'components/common/media/double-link';
import { KNOWN_ACTIONS, MessagingCardActions } from 'components/common/messaging-card/messaging-card-actions';
import { MessageType } from '__generated__/api';
import { MoreDetailsLx } from 'components/common/messaging-card/more-details-lx';
import { TermsAndConditionsDialog } from 'components/common/messaging-card/terms-and-conditions-dialog';

const LinkTracked = ClickTracker(DoubleLink);
export interface MessagingCardProps {
  analytics?: AnalyticsStore;
  className?: string;
  liveAnnounceLevel?: string;
  model: MessagingCardViewModel;
  tag?: string | React.ComponentClass<any> | React.FunctionComponent<any>;
  index?: number;
  uiPage?: UIPageStore;
  testId?: string;
  iconSize?: UitkIconSize;
}

const getMarkModel = model => {
  if (model?.titleModel?.mark) {
    return model.titleModel;
  }
  if (model?.subtitleModel?.mark) {
    return model.subtitleModel;
  }

  return null;
};

export const MessagingCard: React.FunctionComponent<MessagingCardProps> = inject(
  'analytics',
  'uiPage'
)(
  ({
    model,
    analytics,
    className = 'uitk-cell xl-cell-1-3 l-cell-1-3 m-cell-1-2 s-cell-1-1',
    liveAnnounceLevel,
    tag: Tag = 'li',
    uiPage,
    testId = 'messaging-card',
    iconSize = UitkIconSize.MEDIUM,
  }) => {
    if (!model) {
      return null;
    }

    const illustration = model.titleModel?.illustration;
    const icon = model.titleModel?.icon;
    const modelWithMark = getMarkModel(model);

    const iconClasses = 'messaging-card__title-icon all-l-margin-three all-cell-align-middle';
    const markSize = model.type === MessageType.LOGIN ? UitkMarkSize.MEDIUM_LARGE : UitkMarkSize.LARGE;

    return (
      <Tag className={className} aria-live={liveAnnounceLevel} data-stid={testId}>
        <UitkCard>
          <UitkLayoutFlex alignItems="center">
            <UitkSpacing margin={{ inlinestart: 'half' }}>
              <div>
                {illustration && (
                  <UitkSpacing margin={{ inlinestart: 'one' }} padding={{ inline: 'six' }}>
                    <UitkFigure ratio={UitkFigureAspectRatioType.R1_1} src={illustration} />
                  </UitkSpacing>
                )}
                {modelWithMark?.mark && (
                  <UitkMark name={modelWithMark.mark} description={modelWithMark.text} size={markSize} className={iconClasses} />
                )}
                {icon && (
                  <UitkSpacing margin={{ inlinestart: 'three', block: 'three' }}>
                    <div>
                      <UitkIcon name={icon} size={iconSize} spotlight="background" />
                    </div>
                  </UitkSpacing>
                )}
              </div>
            </UitkSpacing>
            <MessagingCardBody analytics={analytics} model={model} uiPage={uiPage} />
          </UitkLayoutFlex>
        </UitkCard>
      </Tag>
    );
  }
);

interface MessagingCardBodyProps {
  model: MessagingCardViewModel;
  analytics?: AnalyticsStore;
  uiPage?: UIPageStore;
}

const MessagingCardDialogs = ({ primaryAction }) => {
  if (
    primaryAction &&
    primaryAction.actionDetails &&
    primaryAction.actionDetails.name &&
    primaryAction.actionDetails.name.trim().toLowerCase() === KNOWN_ACTIONS.TERMS_AND_CONDITIONS
  ) {
    return <TermsAndConditionsDialog action={primaryAction} />;
  } else if (
    primaryAction &&
    primaryAction.actionDetails &&
    primaryAction.actionDetails.name &&
    primaryAction.actionDetails.name.trim().toLowerCase() === KNOWN_ACTIONS.FREE_LX
  ) {
    return <MoreDetailsLx action={primaryAction} />;
  }

  return null;
};

const MessagingCardBody: React.FunctionComponent<MessagingCardBodyProps> = ({ analytics, model, uiPage }) => {
  const titleModel = model.titleModel;
  const subtitleModel = model.subtitleModel;
  const title = titleModel && titleModel.text;
  const subtitle = subtitleModel && subtitleModel.text;
  const actionModel = model.actionModel || {};
  const primaryAction = actionModel.primary || {};
  const secondaryAction = actionModel.secondary || {};
  const history = useHistory();
  const location = useLocation();
  const l10n = useLocalization();
  const showNotification = () => {
    if (uiPage && l10n) {
      uiPage.showNotification(l10n.formatText('coupon.copy.notification'));
    }
  };

  return (
    <>
      <MessagingCardDialogs primaryAction={primaryAction} />
      <div className="uitk-cell all-cell-1-2 all-cell-fill all-cell-align-middle all-y-padding-three all-x-padding-three">
        <UitkCard>
          {title && (
            <UitkHeading tag="h3" size={7}>
              {title}
            </UitkHeading>
          )}

          {subtitle && <div className="messaging-card__subtitle uitk-type-300">{subtitle}</div>}

          {primaryAction.text && (
            <UitkList bullet="no-bullet" orientation="inline" size={2} spacing type="unordered">
              {secondaryAction.text && (
                <UitkListItem data-stid="messaging-card">
                  <UitkLink>
                    <LinkTracked
                      to={secondaryAction.href}
                      rfrr={secondaryAction.rfrr}
                      linkName="Secondary Messaging Action"
                      trackImmediate={true}
                      onClick={MessagingCardActions.resolveAction(
                        history,
                        location,
                        secondaryAction,
                        analytics,
                        showNotification,
                        actionModel.primary.rfrr
                      )}
                    >
                      {secondaryAction.text}
                    </LinkTracked>
                  </UitkLink>
                </UitkListItem>
              )}
              {primaryAction.text && (
                <UitkListItem data-stid="messaging-card">
                  <UitkLink>
                    <LinkTracked
                      to={primaryAction.href}
                      rfrr={primaryAction.rfrr}
                      linkName="Primary Messaging Action"
                      trackImmediate={true}
                      onClick={MessagingCardActions.resolveAction(
                        history,
                        location,
                        primaryAction,
                        analytics,
                        showNotification,
                        primaryAction.href
                      )}
                    >
                      {primaryAction.text}
                    </LinkTracked>
                  </UitkLink>
                </UitkListItem>
              )}
            </UitkList>
          )}
        </UitkCard>
      </div>
    </>
  );
};
MessagingCardBody.displayName = 'MessagingCardBody';
